import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  SimpleGrid,
  Text,
  VStack
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts"
import { getSolarBreakdown, retriggerSolarAnalysis, SolarAnalysis } from "../../services/portfolioApi"
import RoofOrientationVisual from "./RoofOrientationVisual"
import SatelliteView from "./SatelliteView"

import { Home } from "../../services/portfolioApi"
import {
  buildAspectRows,
  getFeatureAnalysis,
  getPanelConfiguration,
  getRowMaxPanels,
  getSolarOverviewMetrics,
  hasMaximumPanelBreakdown,
  sumAspectValues
} from "../../utils/solarAnalysisUtils"
import SummaryStatCard from "./SummaryStatCard"

interface SolarPotentialTabProps {
  solarAnalyses: Array<any>
  homeId: number
  home: Home
  isActive?: boolean
}

const SolarPotentialTab = ({
  solarAnalyses,
  homeId,
  home,
  isActive = false
}: SolarPotentialTabProps) => {
  const [panelConfig, setPanelConfig] = useState<"recommended" | "maximum">("recommended")

  const featureAnalysis = getFeatureAnalysis(solarAnalyses)
  const greenflipSolarAnalysis: SolarAnalysis | null = solarAnalyses.find(
    (item) => item.calculationVersion === "greenflip_v0"
  ) || null
  const queryClient = useQueryClient()

  const retriggerMutation = useMutation({
    mutationFn: () => retriggerSolarAnalysis(homeId),
    onSuccess: () => {
      // Refetch home detail data to get updated solar analysis
      queryClient.invalidateQueries({ queryKey: ["homeDetail", homeId] })
    }
  })

  const {
    recommendedCount,
    maxPanels,
    systemCapacityKw,
    estAnnualGen,
    sunshineHours,
    panelArea,
    existingPanelsKwp
  } = getSolarOverviewMetrics(featureAnalysis)

  // Fallback area from home.features for older analyses without roofArea*Sqm
  const fallbackAreas: Record<string, number> = {
    South: Number((home.features as any).roofShapeAspectAreaFacingSouthM2) || 0,
    Southeast: Number((home.features as any).roofShapeAspectAreaFacingSoutheastM2) || 0,
    Southwest: Number((home.features as any).roofShapeAspectAreaFacingSouthwestM2) || 0,
    East: Number((home.features as any).roofShapeAspectAreaFacingEastM2) || 0,
    West: Number((home.features as any).roofShapeAspectAreaFacingWestM2) || 0,
    North: Number((home.features as any).roofShapeAspectAreaFacingNorthM2) || 0,
    Northeast: Number((home.features as any).roofShapeAspectAreaFacingNortheastM2) || 0,
    Northwest: Number((home.features as any).roofShapeAspectAreaFacingNorthwestM2) || 0,
    Flat: Number((home.features as any).roofTotalFlatSqm) || 0
  }

  const rawAspectRows = buildAspectRows(featureAnalysis)
  const hasSolarAreaData = rawAspectRows.some((r) => r.areaSqm > 0)
  const aspectRows = hasSolarAreaData
    ? rawAspectRows
    : rawAspectRows.map((row) => ({
        ...row,
        areaSqm: row.areaSqm || fallbackAreas[row.label] || 0
      }))
  const { totalPanels, totalOutput } = sumAspectValues(aspectRows)
  const totalArea = aspectRows.reduce((sum, row) => sum + row.areaSqm, 0)
  const maxAspectRows = buildAspectRows(featureAnalysis, "maximum")
  const { totalPanels: totalMaxPanels } = sumAspectValues(maxAspectRows)
  const hasMaxBreakdown = hasMaximumPanelBreakdown(maxAspectRows)

  const { panelWattageDisplay, panelEfficiencyDisplay, panelAreaDisplay } =
    getPanelConfiguration(featureAnalysis)

  const currentVersion = featureAnalysis?.versionUsed ?? featureAnalysis?.calculationVersion ?? "unknown"
  const isGreenflipV0 = currentVersion === "greenflip_v0"
  const hasVisualization = isGreenflipV0 && greenflipSolarAnalysis?.visualisationPublicS3Url?.includes("frontend")

  // Fetch solar breakdown data for both configs on tab load
  const breakdownEnabled = !!localStorage.getItem("token") && homeId > 0 && isActive
  const { data: recommendedBreakdown } = useQuery({
    queryKey: ["solarBreakdown", homeId, "recommended"],
    queryFn: () => getSolarBreakdown(homeId, "recommended"),
    enabled: breakdownEnabled
  })
  const { data: maximumBreakdown } = useQuery({
    queryKey: ["solarBreakdown", homeId, "maximum"],
    queryFn: () => getSolarBreakdown(homeId, "maximum"),
    enabled: breakdownEnabled
  })
  const breakdownData = panelConfig === "maximum" ? maximumBreakdown : recommendedBreakdown

  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ]

  const monthlyChartData = useMemo(() => {
    if (!breakdownData?.totalMonthlyEnergyKwh) return []

    return breakdownData.totalMonthlyEnergyKwh.map((monthData) => ({
      month: monthNames[monthData.month - 1],
      monthNumber: monthData.month,
      energyKwh: Math.round(monthData.energyKwh * 10) / 10
    }))
  }, [breakdownData, panelConfig])

  const maxYAxisValue = useMemo(() => {
    if (!monthlyChartData.length) return 0
    const maxValue = Math.max(...monthlyChartData.map((d) => d.energyKwh))
    return Math.ceil(maxValue * 1.1)
  }, [monthlyChartData])

  return (
    <VStack gap={6} align="stretch">
      {featureAnalysis ? (
        <>
          {/* Overview metrics */}
          <Box bg="white" p={6} borderRadius="lg" position="relative">
            <VStack gap={4} align="stretch">
              <Flex justify="space-between" align="center">
                <Heading size="md">Solar Overview</Heading>
              </Flex>

              {existingPanelsKwp > 0 && (
                <Box
                  bg="blue.50"
                  border="1px solid"
                  borderColor="blue.200"
                  borderRadius="md"
                  px={4}
                  py={3}
                >
                  <Text fontSize="sm" fontWeight="semibold" color="blue.800">
                    Existing Solar Panels Detected
                  </Text>
                  <Text fontSize="sm" color="blue.700">
                    We estimate this property has ~{existingPanelsKwp.toFixed(1)} kWp of existing solar capacity
                    based on aerial imagery. The panel recommendation below has been reduced to account for
                    the existing installation.
                  </Text>
                </Box>
              )}

              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
                <SummaryStatCard
                  title="Max Panels Possible"
                  value={maxPanels.toString()}
                  borderColor="gray.100"
                  subline="Estimated from available roof space"
                />
                <SummaryStatCard
                  title={existingPanelsKwp > 0 ? "Recommended Additional Panels" : "Recommended Panels"}
                  value={`${recommendedCount}`}
                  subline={existingPanelsKwp > 0
                    ? `${panelWattageDisplay}W panels / ${systemCapacityKw}kW new + ${existingPanelsKwp.toFixed(1)}kW existing`
                    : `${panelWattageDisplay}W panels / ${systemCapacityKw}kW total capacity`
                  }
                  borderColor="gray.100"
                />
                <SummaryStatCard
                  title="Estimated Annual Generation"
                  value={
                    estAnnualGen ? `${estAnnualGen.toFixed(0)} kWh` : "N/A"
                  }
                  subline={
                    sunshineHours
                      ? `With ${parseFloat(sunshineHours).toFixed(0)} sunshine hours / year`
                      : undefined
                  }
                  borderColor="gray.100"
                />
              </SimpleGrid>
              <Text fontSize="xs" color="gray.500" textAlign="right">
                Analysis:{" "}
                {new Date(featureAnalysis.createdAt).toLocaleDateString()} •
                Updated:{" "}
                {new Date(featureAnalysis.updatedAt).toLocaleDateString()}
              </Text>
            </VStack>
          </Box>

          {/* Roof Orientation Breakdown with Satellite/Visualization View */}
          <Flex
            direction={{ base: "column", lg: "row" }}
            gap={6}
            align="stretch"
            overflowX="hidden"
          >

            {/* Satellite View or Greenflip Visualization */}
            <Box
              bg="white"
              borderRadius="lg"
              maxW={{ base: "100%", lg: "600px" }}
              flex={{ base: "auto", lg: "0 0 600px" }}
              flexShrink={0}
              w={{ base: "100%", lg: "auto" }}
            >
              {hasVisualization ? (
                <img
                  src={greenflipSolarAnalysis!.visualisationPublicS3Url!}
                  alt="Solar Analysis Visualization"
                  style={{ width: "100%", height: "auto", display: "block", borderRadius: "8px" }}
                />
              ) : (
                <SatelliteView
                  showTitle={false}
                  lat={home.osCoordinatesLat || home.lat}
                  lng={home.osCoordinatesLng || home.lng}
                  address={home.fullAddress}
                />
              )}
            </Box>

            {/* Roof Orientation Breakdown + Roof Facts */}
            <Box bg="white" p={6} borderRadius="lg" flex={1} minW={0}>
              <VStack gap={6} align="stretch">
                <Heading size="lg" textAlign="center">
                  Roof Orientation Breakdown
                </Heading>

                {/* Visual and Data Side by Side */}
                <Box flex="1">
                  <RoofOrientationVisual aspectRows={aspectRows} />
                </Box>
              </VStack>
            </Box>
          </Flex>

          {/* Row 3: Full width Recommended Panel Layout table */}
          <Box bg="white" p={6} borderRadius="lg">
            <VStack gap={4} align="stretch">
              <Heading size="lg">Recommended Panel Layout</Heading>
              <Box overflowX="auto">
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <Box
                        as="th"
                        textAlign="left"
                        p={3}
                        fontWeight="bold"
                        fontSize="sm"
                        color="gray.600"
                      >
                        Aspect
                      </Box>
                      <Box
                        as="th"
                        textAlign="right"
                        p={3}
                        fontWeight="bold"
                        fontSize="sm"
                        color="gray.600"
                      >
                        Surface (m²)
                      </Box>
                      <Box
                        as="th"
                        textAlign="right"
                        p={3}
                        fontWeight="bold"
                        fontSize="sm"
                        color="gray.600"
                      >
                        Max Panels
                      </Box>
                      <th
                        style={{
                          textAlign: "right",
                          padding: "0.75rem",
                          fontWeight: "bold",
                          fontSize: "0.875rem",
                          color: "#718096"
                        }}
                      >
                        Recommended
                      </th>
                      <th
                        style={{
                          textAlign: "right",
                          padding: "0.75rem",
                          fontWeight: "bold",
                          fontSize: "0.875rem",
                          color: "#718096"
                        }}
                      >
                        Est. Output (kWh)
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {aspectRows.map((row) => {
                      const maxRec = maxAspectRows.find((a) => a.label === row.label)
                      const maxCount = getRowMaxPanels({
                        apiMaxPanels: maxRec?.panels ?? 0,
                        hasBreakdown: hasMaxBreakdown,
                        areaSqm: row.areaSqm,
                        panelArea
                      })

                      return (
                        <tr
                          key={row.label}
                          style={{ borderTop: "1px solid #e2e8f0" }}
                        >
                          <Box as="td" p={3} fontWeight="medium">
                            {row.label}
                          </Box>
                          <Box
                            as="td"
                            p={3}
                            textAlign="right"
                            fontFamily="mono"
                            color={row.areaSqm === 0 ? "gray.400" : undefined}
                          >
                            {row.areaSqm.toFixed(1)}
                          </Box>
                          <Box
                            as="td"
                            p={3}
                            textAlign="right"
                            fontFamily="mono"
                            color={maxCount === 0 ? "gray.400" : undefined}
                          >
                            {maxCount}
                          </Box>
                          <Box
                            as="td"
                            p={3}
                            textAlign="right"
                            fontFamily="mono"
                            color={row.panels === 0 ? "gray.400" : undefined}
                          >
                            {row.panels}
                          </Box>
                          <Box
                            as="td"
                            p={3}
                            textAlign="right"
                            fontFamily="mono"
                            color={row.outputKwh === 0 ? "gray.400" : undefined}
                          >
                            {row.outputKwh.toFixed(0)}
                          </Box>
                        </tr>
                      )
                    })}
                    <tr style={{ borderTop: "2px solid #e2e8f0" }}>
                      <Box as="td" p={3} fontWeight="bold">
                        Total
                      </Box>
                      <Box
                        as="td"
                        p={3}
                        textAlign="right"
                        fontFamily="mono"
                        fontWeight="bold"
                        color={totalArea === 0 ? "gray.400" : undefined}
                      >
                        {totalArea.toFixed(1)}
                      </Box>
                      <Box
                        as="td"
                        p={3}
                        textAlign="right"
                        fontFamily="mono"
                        fontWeight="bold"
                        color={(totalMaxPanels || maxPanels) === 0 ? "gray.400" : undefined}
                      >
                        {totalMaxPanels || maxPanels}
                      </Box>
                      <Box
                        as="td"
                        p={3}
                        textAlign="right"
                        fontFamily="mono"
                        fontWeight="bold"
                        color={totalPanels === 0 ? "gray.400" : undefined}
                      >
                        {totalPanels}
                      </Box>
                      <Box
                        as="td"
                        p={3}
                        textAlign="right"
                        fontFamily="mono"
                        fontWeight="bold"
                        color={totalOutput === 0 ? "gray.400" : undefined}
                      >
                        {totalOutput.toFixed(0)}
                      </Box>
                    </tr>
                  </tbody>
                </table>
              </Box>
            </VStack>
          </Box>

          {/* Calculation Variables */}
          <Box bg="white" p={6} borderRadius="lg">
            <VStack gap={4} align="stretch">
              <Heading size="lg">Calculation Variables</Heading>
              <SimpleGrid columns={{ base: 1, md: 4 }} gap={6}>
                <VStack align="start" gap={1}>
                  <Text fontSize="sm" color="gray.600">
                    Panel Wattage
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold">
                    {panelWattageDisplay} W
                  </Text>
                </VStack>
                <VStack align="start" gap={1}>
                  <Text fontSize="sm" color="gray.600">
                    Panel Efficiency
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold">
                    {(panelEfficiencyDisplay * 100).toFixed(1)}%
                  </Text>
                </VStack>
                <VStack align="start" gap={1}>
                  <Text fontSize="sm" color="gray.600">
                    Panel Area
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold">
                    {panelAreaDisplay.toFixed(2)} m²
                  </Text>
                </VStack>
                <VStack align="start" gap={1}>
                  <Text fontSize="sm" color="gray.600">
                    Annual Sunshine Hours
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold">
                    {sunshineHours || "N/A"}
                  </Text>
                </VStack>
              </SimpleGrid>
            </VStack>
          </Box>

          {/* Monthly Solar Production Breakdown */}
          {(monthlyChartData.length > 0 || (breakdownEnabled && !breakdownData)) && (
            <Box bg="white" px={6} py={6} borderRadius="lg">
              <VStack gap={6} align="stretch">
                <Flex justify="space-between" align="center">
                  <Heading size="md">Monthly Solar Production Breakdown</Heading>
                  <HStack gap={1}>
                    <Button
                      size="xs"
                      variant={panelConfig === "recommended" ? "solid" : "outline"}
                      colorPalette="blue"
                      onClick={() => setPanelConfig("recommended")}
                    >
                      Recommended
                    </Button>
                    <Button
                      size="xs"
                      variant={panelConfig === "maximum" ? "solid" : "outline"}
                      colorPalette="blue"
                      onClick={() => setPanelConfig("maximum")}
                    >
                      Maximum
                    </Button>
                  </HStack>
                </Flex>

                <Box width="100%" height="300px">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={monthlyChartData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis
                        dataKey="month"
                        tick={{ fill: "#4a5568", fontSize: 12 }}
                        stroke="#cbd5e0"
                      />
                      <YAxis
                        domain={[0, maxYAxisValue]}
                        tick={{ fill: "#4a5568", fontSize: 12 }}
                        stroke="#cbd5e0"
                        width={60}
                      />
                      <Tooltip
                        content={({ active, payload }: any) => {
                          if (!active || !payload || !payload.length) return null
                          const data = payload[0].payload
                          const value = data.energyKwh

                          return (
                            <Box
                              bg="white"
                              border="1px solid #e2e8f0"
                              borderRadius="6px"
                              p="8px 12px"
                            >
                              <Text fontWeight="bold" mb="4px">
                                {data.month}
                              </Text>
                              <Text>
                                {value.toFixed(1)} kWh
                              </Text>
                            </Box>
                          )
                        }}
                      />
                      <Bar
                        dataKey="energyKwh"
                        fill="#3182ce"
                        radius={[4, 4, 0, 0]}
                        barSize={60}
                      >
                        <LabelList
                          dataKey="energyKwh"
                          position="top"
                          formatter={(value: any) =>
                            `${Number(value).toFixed(0)} kWh`
                          }
                          style={{
                            fill: "#4a5568",
                            fontSize: "11px",
                            fontWeight: "medium"
                          }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

                {/* Total Info */}
                <HStack gap={6} align="start">
                  <VStack align="start" gap={1} flex={1}>
                    <Text fontSize="2xl" fontWeight="bold">
                      {breakdownData?.totalYearlyEnergyKwh.toFixed(0)} kWh/year
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Total Annual Solar Generation ({panelConfig})
                    </Text>
                  </VStack>
                  {(breakdownData?.totalPeakPowerKwp ?? 0) > 0 && (
                    <VStack align="start" gap={1}>
                      <Text fontSize="2xl" fontWeight="bold">
                        {breakdownData?.totalPeakPowerKwp.toFixed(2)} kWp
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        Peak Power
                      </Text>
                    </VStack>
                  )}
                </HStack>
              </VStack>
            </Box>
          )}
        </>
      ) : (
        <Box bg="white" p={6} borderRadius="lg">
          <VStack gap={4} align="stretch">
            <Heading size="lg">Solar Potential Analysis</Heading>
            <Text color="gray.600">
              No solar analysis data available for this home.
            </Text>
            <Button
              colorPalette="blue"
              onClick={() => retriggerMutation.mutate()}
              loading={retriggerMutation.isPending}
              alignSelf="flex-start"
            >
              Re-trigger Solar Analysis
            </Button>
          </VStack>
        </Box>
      )}
    </VStack>
  )
}

export default SolarPotentialTab
